import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setUnauthorizedHandler } from '../api/client';

interface User {
  id: string;
  email: string;
  role?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; organizationName?: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);
const STORAGE_KEY = 'pinsignals.auth';

interface StoredAuth {
  user: User;
  token: string;
}

/**
 * True if the JWT's `exp` claim is in the past, or the token can't be parsed. Lets us
 * reject a stale token on load without a network round-trip (the API has no /auth/me).
 * A token revoked but not yet expired is still caught on the first authenticated request
 * via the global 401 handler.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json) as { exp?: number };
    if (typeof exp !== 'number') return false; // no expiry claim → treat as non-expiring
    return exp * 1000 <= Date.now();
  } catch {
    return true; // malformed token → treat as invalid
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as StoredAuth;
        if (isTokenExpired(parsed.token)) {
          localStorage.removeItem(STORAGE_KEY); // stale session — start logged out
        } else {
          setUser(parsed.user);
          setToken(parsed.token);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setLoading(false);
  }, []);

  const persist = useCallback((next: StoredAuth | null) => {
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setUser(next.user);
      setToken(next.token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      setToken(null);
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<{ user: User; token: string }>('/auth/login', {
        email,
        password,
      });
      persist(result);
    },
    [persist],
  );

  const register = useCallback(
    async (input: { email: string; password: string; organizationName?: string }) => {
      const result = await api.post<{ user: User; token: string }>('/auth/register', input);
      persist(result);
    },
    [persist],
  );

  const logout = useCallback(() => persist(null), [persist]);

  // Let the API client trigger a logout when any authenticated request gets a 401.
  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo<AuthState>(
    () => ({ user, token, loading, login, register, logout }),
    [user, token, loading, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
